import React from 'react';
import { ColumnConfig, SortConfig } from '../../types/cms';

interface CollectionTableProps<T extends { id: string }> {
  data: T[];
  columns: ColumnConfig<T>[];
  selectedIds: Set<string>;
  onSelectAll: () => void;
  onSelectItem: (id: string, shiftKey: boolean) => void;
  sortConfig: SortConfig | null;
  onSort: (field: string) => void;
  renderRow: (item: T, index: number) => React.ReactNode;
  loading?: boolean;
}

export default function CollectionTable<T extends { id: string }>({
  data,
  columns,
  selectedIds,
  onSelectAll,
  onSelectItem,
  sortConfig,
  onSort,
  renderRow,
  loading = false,
}: CollectionTableProps<T>) {
  const allSelected = data.length > 0 && data.every(item => selectedIds.has(item.id));
  const someSelected = data.some(item => selectedIds.has(item.id)) && !allSelected;

  return (
    <div className="overflow-x-auto border border-gray-300 rounded-md">
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-neutral-50">
          <tr>
            {/* Selection checkbox column */}
            <th className="w-12 px-3 py-3">
              <input
                type="checkbox"
                checked={allSelected}
                ref={input => {
                  if (input) {
                    input.indeterminate = someSelected;
                  }
                }}
                onChange={onSelectAll}
                className="w-4 h-4 rounded border-gray-300 text-neutral-900 focus:ring-neutral-900 cursor-pointer"
              />
            </th>

            {/* Column headers */}
            {columns.map((column) => (
              <th
                key={column.key}
                className={`
                  px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider
                  ${column.sortable ? 'cursor-pointer hover:bg-neutral-100 select-none' : ''}
                  ${column.width || ''}
                `}
                onClick={() => column.sortable && onSort(column.key)}
              >
                <div className="flex items-center gap-2">
                  <span>{column.label}</span>
                  {column.sortable && sortConfig?.field === column.key && (
                    <svg
                      className={`w-4 h-4 transition-transform ${
                        sortConfig.direction === 'desc' ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 15l7-7 7 7"
                      />
                    </svg>
                  )}
                </div>
              </th>
            ))}

            {/* Actions column */}
            <th className="w-20 px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>

        <tbody className="bg-white divide-y divide-gray-200">
          {loading ? (
            // Loading skeleton
            Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} className="animate-pulse">
                <td className="px-3 py-4">
                  <div className="w-4 h-4 bg-gray-200 rounded"></div>
                </td>
                {columns.map((col, j) => (
                  <td key={j} className="px-4 py-4">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  </td>
                ))}
                <td className="px-4 py-4">
                  <div className="h-4 bg-gray-200 rounded w-12 ml-auto"></div>
                </td>
              </tr>
            ))
          ) : data.length === 0 ? (
            // Empty state
            <tr>
              <td colSpan={columns.length + 2} className="px-4 py-12 text-center">
                <div className="flex flex-col items-center gap-2">
                  <svg
                    className="w-12 h-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                  </svg>
                  <p className="text-gray-500 text-sm">Aucun élément</p>
                </div>
              </td>
            </tr>
          ) : (
            // Render rows
            data.map((item, index) => renderRow(item, index))
          )}
        </tbody>
      </table>
    </div>
  );
}
